import { Suspense, lazy, useEffect, useState } from "react";

/**
 * Niet-kritieke widgets (zwevende woordenlijst-zoekbalk en het
 * GTM-debugpaneel) worden pas na het load-event of bij de eerste
 * interactie geladen. Ze staan daardoor niet in de initiële JS-bundel
 * en concurreren niet meer met LCP.
 */
const FloatingGlossarySearch = lazy(() =>
  import("@/components/floating-glossary-search").then((m) => ({
    default: m.FloatingGlossarySearch,
  })),
);

const GtmDebugPanel = lazy(() =>
  import("@/components/gtm-debug-panel").then((m) => ({ default: m.GtmDebugPanel })),
);

function useDeferredMount() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      setReady(true);
    };
    const events: Array<keyof WindowEventMap> = [
      "pointerdown",
      "keydown",
      "touchstart",
      "scroll",
    ];
    events.forEach((e) => window.addEventListener(e, go, { once: true, passive: true }));
    const timer = window.setTimeout(go, 2500);
    return () => {
      done = true;
      window.clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, go));
    };
  }, []);

  return ready;
}

export function DeferredWidgets() {
  const ready = useDeferredMount();

  // De HTML bevat alleen de vertaalteksten van de huidige pagina. Zodra de
  // bezoeker iets doet, halen we de rest van de taal binnen zodat een
  // volgende pagina meteen compleet is.
  useEffect(() => {
    if (!ready) return;
    void import("@/i18n").then((m) => m.ensureLang(m.default.language as never));
  }, [ready]);

  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <FloatingGlossarySearch />
      <GtmDebugPanel />
    </Suspense>
  );
}

